import { Button, Dropdown, Icons } from '@commenda/kakashi';
import { useState, type RefObject } from 'react';

import { useReport } from '../context/ReportContext';
import { useReportExports } from '../hooks/useReportExports';
import { BucketMappingSheet } from './BucketMappingSheet';
import { ComponentsMenu } from './ComponentsMenu';
import { PeriodControls } from './PeriodControls';
import { SavedViewsMenu } from './SavedViewsMenu';

/** Bucket mapping is hidden from the toolbar until the design is finalised. */
const SHOW_MAPPING_ACTION = false;

interface IReportToolbarProps {
  canvasRef: RefObject<HTMLDivElement | null>;
}

/** Filters + export row in the Accounting page's toolbar format. */
export const ReportToolbar = ({ canvasRef }: IReportToolbarProps) => {
  const { dataset, filter, setFilter, booksClosedThrough, bucketOverrides } =
    useReport();
  const { exportPdf, exportXlsx, isExporting } = useReportExports(canvasRef);
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const overrideCount = Object.keys(bucketOverrides).length;

  return (
    <div className="flex flex-wrap items-center justify-between gap-md">
      <div className="flex flex-wrap items-center gap-md">
        <PeriodControls
          toolbar
          value={filter}
          onChange={setFilter}
          months={dataset.months.map((m) => m.month)}
          booksClosedThrough={booksClosedThrough}
        />
        <SavedViewsMenu />
        {SHOW_MAPPING_ACTION && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Icons.Dataflow03 className="size-4" />}
            onClick={() => setIsMappingOpen(true)}
          >
            Mapping{overrideCount > 0 ? ` (${overrideCount})` : ''}
          </Button>
        )}
        <ComponentsMenu />
      </div>
      <Dropdown
        align="end"
        menu={{
          groups: [
            {
              items: [
                {
                  label: 'Export PDF',
                  icon: Icons.File06,
                  disabled: isExporting,
                  onClick: exportPdf,
                },
                {
                  label: 'Export Excel',
                  icon: Icons.Download01,
                  onClick: exportXlsx,
                },
              ],
            },
          ],
        }}
      >
        <Button variant="primary" size="sm" disabled={isExporting}>
          {isExporting ? 'Exporting…' : 'Export'}
        </Button>
      </Dropdown>
      <BucketMappingSheet
        open={isMappingOpen}
        onOpenChange={setIsMappingOpen}
      />
    </div>
  );
};
